import React, { useRef, useState } from 'react';
import { Bookmark, Star, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const CourseCard = ({ course, onPlay }) => {
  const [isFavorite, setIsFavorite] = useState(false);

  return (
    <motion.div 
      className="flex-shrink-0 w-64 md:w-80 bg-[#0F172A] rounded-[24px] overflow-hidden border border-white/5 group relative transition-all duration-300 hover:scale-105 shadow-xl"
    >
      <div className="relative aspect-video overflow-hidden">
        <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button 
                onClick={() => onPlay(course)}
                className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white scale-75 group-hover:scale-100 transition-transform shadow-lg shadow-blue-600/40"
            >
                <Play size={20} fill="currentColor" />
            </button>
        </div>
        
        <button 
          onClick={(e) => { e.stopPropagation(); setIsFavorite(!isFavorite); }}
          className={`absolute top-3 right-3 p-2 rounded-xl backdrop-blur-md border border-white/10 transition-all z-10 ${
            isFavorite ? 'bg-blue-600 text-white' : 'bg-black/40 text-white/70 hover:text-white'
          }`}
        >
          <Bookmark size={16} fill={isFavorite ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="p-5 space-y-2">
        <div className="flex justify-between items-center">
            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">{course.category}</span>
            <div className="flex items-center gap-1 text-yellow-500 text-xs font-bold">
                <Star size={10} fill="currentColor" /> {course.rating}
            </div>
        </div>
        <h3 className="text-white font-bold text-sm leading-snug line-clamp-2 h-10">{course.title}</h3>
        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-tighter">{course.instructor}</p>
      </div>
    </motion.div>
  );
};

const Row = ({ title, courses, onPlay }) => {
  const rowRef = useRef(null);
  const [showControls, setShowControls] = useState(false);

  const scroll = (direction) => {
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      rowRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  return (
    <div 
        className="space-y-4 relative group mb-12"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
    >
      <div className="flex items-center justify-between px-6">
        <h2 className="text-xl md:text-2xl font-black text-white tracking-tighter uppercase italic drop-shadow-md">
          {title}
        </h2>
      </div>

      <div className="relative flex items-center px-6">
        
        <button 
            onClick={() => scroll('left')}
            className={`absolute left-2 z-40 w-12 h-12 rounded-full bg-black/60 hover:bg-blue-600 text-white flex items-center justify-center transition-all duration-300 backdrop-blur-md border border-white/10 shadow-2xl hover:scale-110 ${
                showControls ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'
            }`}
        >
            <ChevronLeft size={28} />
        </button>

        
        <div 
          ref={rowRef}
          className="flex gap-5 overflow-x-auto scroll-smooth scrollbar-hide py-4"
        >
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} onPlay={onPlay} />
          ))}
        </div>

        
        <button 
            onClick={() => scroll('right')}
            className={`absolute right-2 z-40 w-12 h-12 rounded-full bg-black/60 hover:bg-blue-600 text-white flex items-center justify-center transition-all duration-300 backdrop-blur-md border border-white/10 shadow-2xl hover:scale-110 ${
                showControls ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'
            }`}
        >
            <ChevronRight size={28} />
        </button>
      </div>
    </div>
  );
};

export default Row;